import { useState, useEffect, useRef, useCallback } from "react";

type UseRevealQueueHook = (
  outline: Partial<Record<string, any>>,
  expectedKeys: string[],
  revealDelay?: number,
  isComplete?: boolean,
) => { revealedKeys: string[] };

export const useRevealQueue: UseRevealQueueHook = (
  outline,
  expectedKeys,
  revealDelay = 7000, // Default delay of 4000ms
  isComplete,
) => {
  const [revealedKeys, setRevealedKeys] = useState<string[]>([]);
  const revealQueue = useRef<string[]>([...expectedKeys]);
  const isProcessing = useRef<boolean>(false);

  // Function to reveal items one by one with a fixed delay
  const processRevealQueue = useCallback(() => {
    if (revealQueue.current.length > 0 && !isProcessing.current) {
      isProcessing.current = true;
      const nextKey = revealQueue.current.shift();
      if (nextKey) {
        setRevealedKeys((prev) => [...prev, nextKey]);
      }
      setTimeout(() => {
        isProcessing.current = false;
        processRevealQueue(); // Continue processing the queue after the delay
      }, revealDelay);
    }
  }, [revealDelay]);

  // Start processing the queue when the first key in outline is present
  useEffect(() => {
    if (
      !isProcessing.current &&
      Object.keys(outline).length > 0 &&
      !isComplete
    ) {
      processRevealQueue();
    }
  }, [processRevealQueue, outline, isComplete]);

  if (isComplete) {
    return { revealedKeys: expectedKeys };
  }

  return { revealedKeys };
};
